import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { createThematicPackageCampaign } from '@features/campaigns/thematicPackage/services/createThematicPackageCampaign.ts';
import { CreateThematicPackageCampaignPayload } from '@features/campaigns/thematicPackage/types/thematicPackage.types.ts';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { routePaths } from '@shared/router/routePaths.ts';
import { CampaignId } from '@shared/types/campaign.ts';

interface UseCreateThematicPackageCampaignProps {
  sourceCampaignId: CampaignId;
}

export function useCreateThematicPackageCampaign({ sourceCampaignId }: UseCreateThematicPackageCampaignProps) {
  const { t } = useTranslation(i18nNameSpace.API);
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: CreateThematicPackageCampaignPayload) =>
      createThematicPackageCampaign(sourceCampaignId, payload),
    onSuccess: (response) => {
      if (!response) {
        return;
      }

      toast.success(
        <SuccessMessage message={t('api.success.campaign.thematicPackageCreated', { campaignName: response.name })} />,
      );

      void navigate(routePaths.campaignBuilderStep03.url(response.id), { replace: true });
    },
  });

  return {
    createThematicPackageCampaignMutation: mutate,
    isCreateThematicPackageCampaignPending: isPending,
  };
}
